import { cssColour, dispose, mountScene, observeScene, openRenderer } from "./renderer.js";
import type { ResourceScope } from "./lifecycle.js";
/**
 * La vue animée de l'orbite (Three.js).
 *
 * Troisième vue, et la première qui n'a rien coûté en plomberie : les
 * commandes de lecture viennent de `player.ts`, la géométrie de
 * `orbit-scene.ts`, et il ne reste ici que la traduction en triangles. C'est
 * ce que la page générique promettait, vérifié une fois de plus.
 *
 * ## Ce qu'il faut regarder
 *
 * Pas la forme d'une orbite — les deux modèles dessinent des courbes d'allure
 * voisine sur un tour. Ce qui les sépare est la **fermeture** : l'orbite de
 * Newton repasse exactement sur elle-même, celle à exposant libre tourne un
 * peu plus loin à chaque tour et dessine une rosace. Le sillage est donc la
 * pièce principale de cette vue, pas une décoration : sans lui, la différence
 * serait invisible.
 *
 * ## Le sillage se dévoile, il ne se reconstruit pas
 *
 * La ligne est bâtie une fois avec le chemin entier, et `setDrawRange` n'en
 * révèle qu'une part croissante. Reconstruire deux mille points par image
 * ferait exactement ce que la vue de la chute faisait avant correction :
 * allouer pour jeter, soixante fois par seconde.
 */

import type { OrbitScene, OrbitSceneRequest, Vec3 } from "./orbit-scene.js";
import type { SceneHandle } from "../experiment/types.js";
import { createOrbitSceneBuilder, orbitCentre, orbitScenePoints } from "./orbit-scene.js";
import { bindPlayer, renderPlayerControls } from "./player.js";
import { escape, escapeAttribute } from "./format.js";
import { fr } from "../i18n/fr.js";

type ThreeModule = typeof import("three");

/** Le plan orbital vu de biais : assez haut pour lire la rosace, assez bas pour rester une scène. */
const VIEW_DIRECTION = { x: 0.05, y: 0.62, z: 1 } as const;
const FIELD_OF_VIEW = 40;
const FRAMING_MARGIN = 1.1;

export interface OrbitPlayback {
  readonly duration: () => number;
  readonly at: (elapsed: number) => OrbitSceneRequest;
}

// ── Coquille HTML ────────────────────────────────────────────────────────────

export function renderOrbitShell(
  bodies: readonly { readonly id: string; readonly label: string }[],
): string {
  const legend = bodies
    .map((body, rank) =>
      [
        `<li data-nature="predicted" data-model="${escapeAttribute(body.id)}"`,
        ` data-series="${rank}">${escape(body.label)}</li>`,
      ].join(""),
    )
    .join("");

  return [
    `<figure class="verlet-scene">`,
    renderPlayerControls("orbit"),
    `<div class="verlet-scene__stage verlet-scene__stage--tall" data-orbit-stage role="img"`,
    ` aria-label="${escapeAttribute(fr.orbitSceneAlt)}">`,
    `<p class="verlet-scene__fallback">${escape(fr.schematicLoading)}</p>`,
    `</div>`,
    `<ul class="verlet-legend">${legend}</ul>`,
    `<figcaption class="verlet-caveat">${escape(fr.orbitSceneCaveat)}</figcaption>`,
    `</figure>`,
  ].join("");
}

// ── Montage ──────────────────────────────────────────────────────────────────

export function mountOrbitScene(root: ParentNode, playback: OrbitPlayback): SceneHandle {
  return mountScene(root, "[data-orbit-stage]", fr.schematicUnavailable, (scope) => attach(root, playback, scope));
}

interface BuiltOrbit {
  readonly group: InstanceType<ThreeModule["Group"]>;
  readonly spheres: InstanceType<ThreeModule["Mesh"]>[];
  readonly trails: InstanceType<ThreeModule["Line"]>[];
}

async function attach(
  root: ParentNode,
  playback: OrbitPlayback,
  scope: ResourceScope,
): Promise<{ readonly redraw: () => void } | undefined> {
  const stage = root.querySelector<HTMLElement>("[data-orbit-stage]");
  if (stage === null) return;

  const opened = await openRenderer(stage, scope);
  if (!opened) return;
  const { three, renderer } = opened;

  const scene = new three.Scene();
  scope.own(() => dispose(scene));
  const camera = new three.PerspectiveCamera(FIELD_OF_VIEW, 1, 0.01, 100);
  scene.add(camera);

  const ambient = new three.AmbientLight(0xffffff, 1.5);
  const key = new three.DirectionalLight(0xffffff, 1.4);
  key.position.set(0.4, 1, 0.5);
  scene.add(ambient);
  scene.add(key);

  let content = new three.Group();
  scene.add(content);

  let spheres: InstanceType<ThreeModule["Mesh"]>[] = [];
  let trails: InstanceType<ThreeModule["Line"]>[] = [];
  let builtFor: OrbitScene["bounds"] | undefined;
  const prepareScene = createOrbitSceneBuilder();
  let framedAspect = 0;
  let lastElapsed = 0;

  /**
   * La scène ne se reconstruit que si le **chemin** change — un exposant, une
   * distance, une vitesse. Avancer dans le temps ne fait que dévoiler le
   * sillage et déplacer les corps.
   */
  const draw = (): void => {
    const built = prepareScene(playback.at(lastElapsed));
    const signature = built.bounds;

    if (signature !== builtFor) {
      scene.remove(content);
      dispose(content);
      const rebuilt = build(three, built);
      content = rebuilt.group;
      spheres = rebuilt.spheres;
      trails = rebuilt.trails;
      builtFor = signature;
      framedAspect = 0;
      scene.add(content);
    }

    built.bodies.forEach((body, index) => {
      spheres[index]?.position.set(body.centre.x, body.centre.y, body.centre.z);
      trails[index]?.geometry.setDrawRange(0, body.revealed);
    });

    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (width === 0 || height === 0) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    // Le cadrage est celui de la trajectoire **complète**, calculé une fois :
    // le suivre au fil du sillage ferait reculer la caméra pendant le
    // mouvement, ce qui se lit comme un zoom involontaire.
    if (camera.aspect !== framedAspect) {
      frame(three, camera, built, camera.aspect);
      framedAspect = camera.aspect;
    }
    renderer.render(scene, camera);
  };

  const player = bindPlayer(root, playback.duration, (elapsed) => {
    lastElapsed = elapsed;
    draw();
  });
  scope.own(player.detach);

  const onThemeChange = (): void => {
    builtFor = undefined;
    draw();
  };
  observeScene(scope, stage, draw, onThemeChange);

  scope.own(() => {
    scene.remove(content);
    dispose(content);
  });

  return { redraw: player.redraw };
}

// ── Construction ─────────────────────────────────────────────────────────────

const SERIES_VARIABLES = ["--series-a", "--series-b", "--series-c"] as const;
const SERIES_FALLBACKS = ["#2c5f5a", "#8a4b12", "#4a4668"] as const;

function seriesColour(three: ThreeModule, rank: number): InstanceType<ThreeModule["Color"]> {
  const index = rank % SERIES_VARIABLES.length;
  return new three.Color(
    cssColour(SERIES_VARIABLES[index] ?? "--ink", SERIES_FALLBACKS[index] ?? "#1b1a17"),
  );
}

function build(three: ThreeModule, scene: OrbitScene): BuiltOrbit {
  const group = new three.Group();
  const spheres: InstanceType<ThreeModule["Mesh"]>[] = [];
  const trails: InstanceType<ThreeModule["Line"]>[] = [];

  // L'étoile n'appartient à aucun des deux modèles : lui donner la couleur de
  // l'un d'eux la ferait lire comme sa prédiction. Elle est donc neutre.
  const star = new three.Color(cssColour("--ink-soft", "#5c584f"));

  // Le corps central. Il n'est pas à l'échelle, et il ne prétend pas l'être :
  // une étoile à l'échelle d'une orbite serait un point invisible.
  const central = new three.Mesh(
    new three.SphereGeometry(scene.centralRadius, 32, 24),
    new three.MeshLambertMaterial({ color: star }),
  );
  central.position.copy(vector(three, orbitCentre()));
  group.add(central);

  scene.bodies.forEach((body, rank) => {
    const colour = seriesColour(three, rank);

    const geometry = new three.BufferGeometry().setFromPoints(
      body.trail.map((point) => vector(three, point)),
    );
    geometry.setDrawRange(0, body.revealed);
    const trail = new three.Line(
      geometry,
      new three.LineBasicMaterial({ color: colour, transparent: true, opacity: 0.85 }),
    );
    group.add(trail);
    trails.push(trail);

    const sphere = new three.Mesh(
      new three.SphereGeometry(body.radius, 24, 18),
      new three.MeshLambertMaterial({ color: colour }),
    );
    sphere.position.copy(vector(three, body.centre));
    group.add(sphere);
    spheres.push(sphere);
  });

  return { group, spheres, trails };
}

function vector(three: ThreeModule, v: Vec3): InstanceType<ThreeModule["Vector3"]> {
  return new three.Vector3(v.x, v.y, v.z);
}

// ── Cadrage ──────────────────────────────────────────────────────────────────

function frame(
  three: ThreeModule,
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>,
  scene: OrbitScene,
  aspect: number,
): void {
  const centre = vector(three, orbitCentre());

  const direction = new three.Vector3(
    VIEW_DIRECTION.x,
    VIEW_DIRECTION.y,
    VIEW_DIRECTION.z,
  ).normalize();
  const forward = direction.clone().negate();
  const right = new three.Vector3()
    .crossVectors(forward, new three.Vector3(0, 1, 0))
    .normalize();
  const up = new three.Vector3().crossVectors(right, forward);

  const halfVertical = ((FIELD_OF_VIEW / 2) * Math.PI) / 180;
  const halfHorizontal = Math.atan(Math.tan(halfVertical) * aspect);
  const tanHorizontal = Math.tan(halfHorizontal);
  const tanVertical = Math.tan(halfVertical);

  let distance = 0;
  let depth = 0;
  for (const point of orbitScenePoints(scene)) {
    const offset = vector(three, point).sub(centre);
    const along = offset.dot(direction);
    distance = Math.max(
      distance,
      along + (Math.abs(offset.dot(right)) * FRAMING_MARGIN) / tanHorizontal,
      along + (Math.abs(offset.dot(up)) * FRAMING_MARGIN) / tanVertical,
    );
    depth = Math.max(depth, Math.abs(along));
  }

  camera.position.copy(centre).add(direction.multiplyScalar(distance));
  camera.lookAt(centre);
  camera.near = Math.max(0.01, distance - depth * 1.5);
  camera.far = distance + depth * 1.5;
  camera.updateProjectionMatrix();
}

// ── Nettoyage ────────────────────────────────────────────────────────────────
